import {
  Component,
  ChangeDetectionStrategy,
  input,
  inject,
  ElementRef,
  afterNextRender,
  effect,
  OnDestroy,
} from '@angular/core';

@Component({
  selector: 'andor-volume-icon',
  standalone: true,
  template: `<ng-content></ng-content>`,
  styleUrl: './volume-icon.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VolumeIcon implements OnDestroy {
  /** Volume level percentage (0–100). Controls the clip-path fill height. */
  level = input(0);

  /** Fill color for the volume overlay. */
  fillColor = input('#22c55e');

  private hostEl = inject(ElementRef);
  private overlayEl: HTMLElement | null = null;
  private observer: MutationObserver | null = null;

  constructor() {
    afterNextRender(() => {
      const host = this.hostEl.nativeElement;
      this.syncOverlaySvg();

      this.observer = new MutationObserver(() => this.syncOverlaySvg());
      this.observer.observe(host, { childList: true, subtree: true });
    });

    effect(() => {
      const lvl = this.level();
      if (this.overlayEl) {
        this.overlayEl.style.clipPath = `inset(${100 - lvl}% 0 0 0)`;
      }
    });
  }

  ngOnDestroy() {
    this.observer?.disconnect();
  }

  private syncOverlaySvg() {
    const host = this.hostEl.nativeElement;
    const svg = host.querySelector(':scope > :not(.volume-overlay) svg') as SVGElement | null;
    if (!svg) return;

    // Pause observer to avoid infinite loop from our own DOM mutation
    this.observer?.disconnect();

    if (!this.overlayEl) {
      this.overlayEl = document.createElement('div');
      this.overlayEl.className = 'volume-overlay';
      Object.assign(this.overlayEl.style, {
        position: 'absolute',
        inset: '0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        clipPath: 'inset(100% 0 0 0)',
        transition: 'clip-path 100ms linear',
      });
      host.appendChild(this.overlayEl);
    }

    const computedFontSize = getComputedStyle(svg).fontSize;
    if (computedFontSize) {
      this.overlayEl.style.fontSize = computedFontSize;
    }

    const clonedSvg = svg.cloneNode(true) as SVGElement;
    clonedSvg.querySelectorAll('path').forEach((p) => (p.style.fill = this.fillColor()));
    this.overlayEl.replaceChildren(clonedSvg);

    // Re-observe after our mutation is done
    this.observer?.observe(host, { childList: true, subtree: true });
  }
}
